import {
  format,
  isValid,
} from 'date-fns'
import {
  includesText,
  matchesDateRange,
} from '@components/general/DataTable/filters'
import {
  rankItem,
  rankings,
} from '@tanstack/match-sorter-utils'
import Device from '@data/device/model/device'
import {
  DEVICE_STATES,
  isCountedAsActive,
} from '@data/device/model/deviceState'

// Moved to the shared DataTable module; re-exported so existing imports keep working.
export {
  includesText,
  matchesDateRange,
  parseTableDate,
  readDateParam,
} from '@components/general/DataTable/filters'

const SHARED_FILTER_PARAMS = [
  'deviceName',
  'clientMeterId',
  'meterType',
  'accountName',
  'locationName',
  'locationCountry',
  'fromDate',
  'toDate',
  'deviceSettings.type',
  'batteryLevel',
  'batteryStatus',
  'signalStrength',
  'deviceStatus',
  'dlId',
  'dUuid',
  'installedValves',
  'deviceSettings.hasValveSchedule',
]

const ADMIN_FILTER_PARAMS = [
  'companyAlias',
  'deviceSettings.report30Day',
  'deviceSettings.pulsesPerLiter',
  'firmware',
  'ICCID',
  'mvno',
  'networkName',
  'deviceSettings.deviceState',
  'deviceVendorName',
  'deviceSettings.monitoringEnabled',
  'deviceSettings.aasEnabled',
  'deviceSettings.usageLoggingEnabled',
  'deviceSettings.vacant',
  'deviceSettings.noFlowThresholdHours',
  'deviceSettings.missedCheckinThreshold',
  'deviceSettings.uploadFreqMins',
  'lastPingFrom',
  'lastPingTo',
]

export interface DeviceInformationRow {
  device: Device
  accountName: string
  locationName: string
  locationCountry: string
  companyAlias: string
  installedValves: number
  powerStatus: string
  batteryBands: string[]
  signalBand: string
  deviceStatus: string
  installDate?: number
  lastPing?: number
  subRows?: DeviceInformationRow[]
  contextOnly?: boolean
}

// Both timestamp columns sort on the value this returns, and both declare
// `sortUndefined: 'last'`. That option tests for `undefined` only, so a missing date has to
// arrive as `undefined` and never as `NaN` — `new Date(undefined).getTime()` is `NaN`, and NaN
// makes TanStack's `compareBasic` return -1 for every comparison including NaN against NaN,
// which is a non-transitive comparator and orders those rows arbitrarily rather than last.
export const toSortableTime = (value?: Date | string | number | null): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined

  const time = value instanceof Date ? value.getTime() : new Date(value).getTime()
  return Number.isFinite(time) ? time : undefined
}

// Multi-select filters are stored as comma-separated values
// (`?deviceSettings.deviceState=active,suspended`, `?installedValves=0,2`). Unknown values,
// e.g. from a hand-edited URL, are dropped rather than matching nothing. A single value, the
// format the single-select filters wrote, parses as a one-item list.
export const readListParam = <T extends string>(
  value: string | null,
  allowed: readonly T[],
): T[] =>
  (value ?? '')
    .split(',')
    .filter((item): item is T => (allowed as readonly string[]).includes(item))

export const INSTALLED_VALVE_VALUES = ['0', '1', '2', '3', '4', '5'] as const

// Only these vendors report a connection status the Status column can show; every other
// device renders a ban icon there.
const STATUS_VENDORS = ['SMARTFLOW', 'METASPHERE', 'PLUM']

// What the Status cell displays: 'Online', 'Offline', or '' for a ban icon. The online
// summary counts through this too, so its figures match the column by construction.
export const getDisplayedStatus = (row: DeviceInformationRow) =>
  STATUS_VENDORS.includes(row.device.deviceVendor) ? row.deviceStatus : ''

// Only devices in a state that counts as active (the same rule as the Home dashboard) are
// summarised. The Status column itself still shows a status for every state.
export const summarizeOnlineStatus = (rows: DeviceInformationRow[]) => {
  let online = 0
  let total = 0

  rows.forEach((row) => {
    if (!isCountedAsActive(row.device.deviceSettings.deviceState)) return

    const status = getDisplayedStatus(row)
    if (status === 'Online') online++
    if (status === 'Online' || status === 'Offline') total++
  })

  return { online, total }
}

export const getDeviceMeterType = (device: Device) =>
  device.deviceSettings.masterDeviceIdRef !== undefined &&
  device.deviceSettings.masterDeviceIdRef !== null
    ? 'Submeter'
    : 'Main meter'

export const formatTableDate = (
  value?: Date | number | string,
  includeTime = false,
) => {
  if (value === undefined || value === null || value === '') return '-'

  const date = value instanceof Date ? value : new Date(value)
  if (!isValid(date)) return '-'

  return format(date, includeTime ? 'dd-MM-yyyy HH:mm' : 'dd-MM-yyyy')
}

// Below this length a token subsequence-matches nearly every name, so the fuzzy tier is not
// engaged for it and it must match as a substring instead. Set conservatively on the noise
// side: the cost of being too high is that a short mistyped token behaves like substring and
// finds nothing, which is less damaging than a page of noise and easier to relax later.
export const FUZZY_MIN_QUERY_LENGTH = 4

// The rank given to any substring hit, including a match on an identifier rather than a name.
// It puts a pasted UUID among strong matches instead of below weak fuzzy guesses. An assumption
// rather than a derived value — see the fuzzy search specification.
const SUBSTRING_MATCH_RANK = rankings.CONTAINS

interface NameFieldMatch {
  passed: boolean
  rank: number
}

// Shared by filtering and ranking so the two cannot disagree about what matched. A field with
// no query passes and contributes no rank, which keeps the max below meaningful.
const matchNameField = (
  value: string | number | undefined,
  query: string | null,
  fuzzy: boolean,
): NameFieldMatch => {
  const trimmed = query?.trim() ?? ''
  if (!trimmed) {
    return {
      passed: true,
      rank: 0,
    }
  }

  if (!fuzzy) {
    const passed = includesText(value, trimmed)
    return {
      passed,
      rank: passed ? SUBSTRING_MATCH_RANK : 0,
    }
  }

  // Matched per whitespace-separated token, every token required. `rankItem` on the whole query
  // is order-sensitive — 'plant room' is not a subsequence of 'Room 3 Plant' — so matching the
  // query as one string would miss the word-order case that motivated fuzzy search in the first
  // place. Tokenising also lets the length threshold protect each token rather than only the
  // whole query, so 'ab cd' cannot slip past it by being four characters long overall.
  const haystack = String(value ?? '')
  const tokens = trimmed.split(/\s+/)
  let weakest = Number.POSITIVE_INFINITY

  for (const token of tokens) {
    if (token.length >= FUZZY_MIN_QUERY_LENGTH) {
      const ranked = rankItem(haystack, token)
      if (!ranked.passed) {
        return {
          passed: false,
          rank: 0,
        }
      }
      weakest = Math.min(weakest, ranked.rank)
      continue
    }

    if (!includesText(haystack, token)) {
      return {
        passed: false,
        rank: 0,
      }
    }
    weakest = Math.min(weakest, SUBSTRING_MATCH_RANK)
  }

  // A row is only as good as its weakest token, so that a query where one word barely matches
  // does not outrank one where every word matches well.
  return {
    passed: true,
    rank: Number.isFinite(weakest) ? weakest : SUBSTRING_MATCH_RANK,
  }
}

const matchesDeviceIdentifiers = (
  device: Device,
  query: string | null,
) => Boolean(query) && [
  device.deviceId,
  device.dUuid,
  device.dlId,
].some((value) => includesText(value, query))

const matchesBooleanFilter = (value: boolean, filterValue: string | null) => {
  if (!filterValue) return true
  return filterValue === 'Yes' ? value : !value
}

const matchesNumberFilter = (value: number, filterValue: string | null) => {
  if (!filterValue) return true

  const numericFilter = Number(filterValue)
  return Number.isFinite(numericFilter) && value === numericFilter
}

export const hasActiveDeviceFilters = (
  searchParams: URLSearchParams,
  isAdmin: boolean,
) => {
  const params = isAdmin
    ? [...SHARED_FILTER_PARAMS, ...ADMIN_FILTER_PARAMS]
    : SHARED_FILTER_PARAMS

  return params.some((param) => Boolean(searchParams.get(param)))
}

export const filterDeviceInformationRows = (
  rows: DeviceInformationRow[],
  searchParams: URLSearchParams,
  isAdmin: boolean,
  fuzzy = false,
) => rows.filter((row) => {
  const { device } = row
  const deviceSearch = searchParams.get('deviceName')
  // Only the name is matched fuzzily. The identifiers stay on substring in both modes, so a
  // pasted UUID returns exactly one row whether the toggle is on or off.
  const matchesDeviceSearch = !deviceSearch ||
    matchNameField(device.deviceName, deviceSearch, fuzzy).passed ||
    matchesDeviceIdentifiers(device, deviceSearch)

  if (!matchesDeviceSearch) return false
  if (!includesText(
    device.deviceSettings.clientMeterId,
    searchParams.get('clientMeterId'),
  )) return false
  if (
    searchParams.get('meterType') &&
    getDeviceMeterType(device) !== searchParams.get('meterType')
  ) return false
  if (!matchNameField(row.accountName, searchParams.get('accountName'), fuzzy).passed) return false
  if (!includesText(row.locationName, searchParams.get('locationName'))) return false
  if (
    searchParams.get('locationCountry') &&
    row.locationCountry !== searchParams.get('locationCountry')
  ) return false
  if (!matchesDateRange(
    device.deviceSettings.installDate,
    searchParams.get('fromDate'),
    searchParams.get('toDate'),
  )) return false
  if (
    searchParams.get('deviceSettings.type') &&
    device.deviceSettings.type !== searchParams.get('deviceSettings.type')
  ) return false
  if (
    searchParams.get('batteryStatus') &&
    row.powerStatus !== searchParams.get('batteryStatus')
  ) return false
  // A device shows an internal reading and, when it has one, an external reading too, so it can
  // display two bands at once. Matching on either keeps every selection consistent with what is
  // on screen — a device visibly showing Critical is returned by Critical even if its other
  // battery is Excellent. No readings means no bands, so it matches nothing.
  const batteryBand = searchParams.get('batteryLevel')
  if (batteryBand && !row.batteryBands.includes(batteryBand)) return false
  // Matches the band SignalIcon renders, not the raw CSQ number, so a selection always
  // corresponds to something visible in the cell. A device with no reading has no band and is
  // therefore excluded by every selection, the same way an underivable power status is.
  if (
    searchParams.get('signalStrength') &&
    row.signalBand !== searchParams.get('signalStrength')
  ) return false
  if (
    searchParams.get('deviceStatus') &&
    row.deviceStatus !== searchParams.get('deviceStatus')
  ) return false
  if (!includesText(device.dlId, searchParams.get('dlId'))) return false
  if (!includesText(device.dUuid, searchParams.get('dUuid'))) return false
  const valveCounts = readListParam(searchParams.get('installedValves'), INSTALLED_VALVE_VALUES)
  if (
    valveCounts.length > 0 &&
    !(valveCounts as string[]).includes(String(row.installedValves))
  ) return false
  if (!matchesBooleanFilter(
    device.deviceSettings.hasValveSchedule,
    searchParams.get('deviceSettings.hasValveSchedule'),
  )) return false

  if (!isAdmin) return true

  if (!includesText(row.companyAlias, searchParams.get('companyAlias'))) return false
  if (!matchesBooleanFilter(
    device.deviceSettings.report30Day,
    searchParams.get('deviceSettings.report30Day'),
  )) return false
  if (
    searchParams.get('deviceSettings.pulsesPerLiter') &&
    String(device.deviceSettings.pulsesPerLiter) !==
      searchParams.get('deviceSettings.pulsesPerLiter')
  ) return false
  if (!includesText(device.firmware, searchParams.get('firmware'))) return false
  if (!includesText(device.ICCID, searchParams.get('ICCID'))) return false
  if (
    searchParams.get('mvno') &&
    (device.mvno || '-') !== searchParams.get('mvno')
  ) return false
  if (!includesText(device.networkName, searchParams.get('networkName'))) return false
  const deviceStates = readListParam(searchParams.get('deviceSettings.deviceState'), DEVICE_STATES)
  if (
    deviceStates.length > 0 &&
    !deviceStates.includes(device.deviceSettings.deviceState)
  ) return false
  if (
    searchParams.get('deviceVendorName') &&
    device.deviceVendorName !== searchParams.get('deviceVendorName')
  ) return false
  if (!matchesBooleanFilter(
    device.deviceSettings.monitoringEnabled,
    searchParams.get('deviceSettings.monitoringEnabled'),
  )) return false
  if (!matchesBooleanFilter(
    device.deviceSettings.aasEnabled,
    searchParams.get('deviceSettings.aasEnabled'),
  )) return false
  if (!matchesBooleanFilter(
    device.deviceSettings.usageLoggingEnabled,
    searchParams.get('deviceSettings.usageLoggingEnabled'),
  )) return false
  if (!matchesBooleanFilter(
    device.deviceSettings.vacant,
    searchParams.get('deviceSettings.vacant'),
  )) return false
  if (!matchesNumberFilter(
    device.deviceSettings.noFlowThresholdHours,
    searchParams.get('deviceSettings.noFlowThresholdHours'),
  )) return false
  if (!matchesNumberFilter(
    device.deviceSettings.missedCheckinThreshold,
    searchParams.get('deviceSettings.missedCheckinThreshold'),
  )) return false
  if (!matchesNumberFilter(
    device.deviceSettings.uploadFreqMins,
    searchParams.get('deviceSettings.uploadFreqMins'),
  )) return false

  return matchesDateRange(
    row.lastPing,
    searchParams.get('lastPingFrom'),
    searchParams.get('lastPingTo'),
  )
})

// Rank is query-dependent, so it cannot live on the row model, which is built before any filter
// is read. It is produced separately and only when rank ordering is actually going to be used,
// over the already-filtered subset. Sharing `matchNameField` with the filter keeps the two from
// disagreeing about what matched.
export const rankDeviceInformationRows = (
  rows: DeviceInformationRow[],
  searchParams: URLSearchParams,
  fuzzy: boolean,
) => {
  const nameQuery = searchParams.get('deviceName')
  const accountQuery = searchParams.get('accountName')
  const ranks = new Map<string, number>()

  rows.forEach((row) => {
    const name = matchNameField(row.device.deviceName, nameQuery, fuzzy)
    const account = matchNameField(row.accountName, accountQuery, fuzzy)
    const identifier = matchesDeviceIdentifiers(row.device, nameQuery)

    ranks.set(row.device.dUuid, Math.max(
      name.rank,
      account.rank,
      identifier ? SUBSTRING_MATCH_RANK : 0,
    ))
  })

  return ranks
}

// Array.prototype.sort is stable, so equal ranks keep their incoming order and the result is
// only reordered where relevance actually differs.
export const orderRowsByRank = (
  rows: DeviceInformationRow[],
  ranks: Map<string, number>,
) => [...rows].sort(
  (a, b) => (ranks.get(b.device.dUuid) ?? 0) - (ranks.get(a.device.dUuid) ?? 0),
)

// Roots order by the best rank anywhere in their group, children order within it. The hierarchy
// is untouched: a child never leaves its master, only the order inside changes.
export const orderGroupedRowsByRank = (
  groups: DeviceInformationRow[],
  ranks: Map<string, number>,
) => {
  const bestRank = (group: DeviceInformationRow) => Math.max(
    ranks.get(group.device.dUuid) ?? 0,
    ...(group.subRows ?? []).map((child) => ranks.get(child.device.dUuid) ?? 0),
  )

  return groups
    .map((group) => (group.subRows && group.subRows.length > 0
      ? {
        ...group,
        subRows: orderRowsByRank(group.subRows, ranks),
      }
      : group))
    .sort((a, b) => bestRank(b) - bestRank(a))
}

export const buildGroupedDeviceRows = (
  rows: DeviceInformationRow[],
  matchingIds: Set<string>,
  hasActiveFilters: boolean,
) => {
  const rowsByDlId = new Map(rows.map((row) => [row.device.dlId, row]))
  const childrenByMasterId = new Map<string, DeviceInformationRow[]>()
  const attachedIds = new Set<string>()

  rows.forEach((row) => {
    const masterRef = row.device.deviceSettings.masterDeviceIdRef
    if (masterRef === undefined || masterRef === null) return

    const master = rowsByDlId.get(masterRef)
    if (
      !master ||
      master.device.dUuid === row.device.dUuid ||
      (
        master.device.deviceSettings.masterDeviceIdRef !== undefined &&
        master.device.deviceSettings.masterDeviceIdRef !== null
      )
    ) return

    const children = childrenByMasterId.get(master.device.dUuid) ?? []
    children.push(row)
    childrenByMasterId.set(master.device.dUuid, children)
    attachedIds.add(row.device.dUuid)
  })

  return rows
    .filter((row) => !attachedIds.has(row.device.dUuid))
    .flatMap((row) => {
      const children = childrenByMasterId.get(row.device.dUuid) ?? []
      if (!hasActiveFilters) return [{ ...row, subRows: children }]

      const rowMatches = matchingIds.has(row.device.dUuid)
      const matchingChildren = children.filter((child) =>
        matchingIds.has(child.device.dUuid),
      )

      if (rowMatches) return [{ ...row, subRows: children }]
      if (matchingChildren.length > 0) {
        return [{
          ...row,
          subRows: matchingChildren,
          contextOnly: true,
        }]
      }

      return []
    })
}
